import React from 'react';
import { motion } from 'framer-motion';
import { Download, CreditCard } from 'lucide-react';
import { Platform } from '../types';

interface PassCardProps {
  platform: Platform;
  brandTitle: string;
  bannerUrl?: string;
  onAddToWallet: () => void;
  isLoading?: boolean;
  hideButton?: boolean;
  showGooglePayContainer?: boolean;
}

const PassCard: React.FC<PassCardProps> = ({
  platform,
  brandTitle,
  bannerUrl,
  onAddToWallet,
  isLoading = false,
  hideButton = false,
  showGooglePayContainer = false,
}) => {
  return (
    <motion.div
      className="flex flex-col justify-between mobile-padding py-6 sm:py-8 lg:py-10 gap-6 sm:gap-8 lg:gap-10 h-[90vh] max-w-4xl mx-auto landscape-compact ultra-compact desktop-centered tablet-optimized"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8">
        <motion.div
          className="flex flex-col items-center justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-[Agency] text-gray-900 mb-2 text-center responsive-text">Hello,</h1>
          <p className="text-sm-responsive text-gray-600 text-center font-[DMSans] px-4">
            Bienvenue dans l'expérience{' '}
            <span className="font-semibold text-yellow-600">{brandTitle}</span> !
          </p>
        </motion.div>

        {bannerUrl && (
          <motion.img
            src={bannerUrl}
            alt={`${brandTitle} banner`}
            className="w-full rounded-lg shadow-lg max-h-40 sm:max-h-52 lg:max-h-64 object-cover"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          />
        )}

        <motion.div
          className="flex flex-col font-[DMSans] text-sm-responsive gap-2 sm:gap-2.5 text-gray-700 px-2 sm:px-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <span className="font-medium text-center sm:text-left">
            Nous sommes ravis de vous compter parmi nous.
          </span>
          <p className="text-xs-responsive text-gray-500 leading-relaxed text-center sm:text-left">
            Afin de terminer votre inscription au programme de fidélité, veuillez
            cliquer sur le bouton ci-dessous pour ajouter votre carte de
            fidélité à votre Wallet.
          </p>
        </motion.div>
      </div>

      <motion.div
        className="flex flex-col items-center gap-4 sm:gap-6 px-2 sm:px-4 pb-safe"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
      >
        {!hideButton && (
          <button
            onClick={onAddToWallet}
            disabled={isLoading}
            className={`flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 rounded-lg sm:rounded-xl font-medium text-white transition-all duration-300 transform w-full max-w-sm text-sm-responsive touch-target ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : platform === 'apple'
                ? 'bg-black hover:bg-gray-800 hover:scale-105 shadow-lg'
                : 'bg-blue-600 hover:bg-blue-700 hover:scale-105 shadow-lg'
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Préparation...</span>
              </>
            ) : (
              <>
                {platform === 'apple' ? (
                  <>
                    <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Ajouter à Apple Wallet</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Générer le bouton Google Pay</span>
                  </>
                )}
              </>
            )}
          </button>
        )}

        {/* Google Pay button container - positioned to replace the blue button */}
        {platform === 'google' && showGooglePayContainer && (
          <div
            id="google-pay-button"
            className="flex justify-center items-center w-full max-w-sm touch-target"
          />
        )}

        <div className="flex flex-col items-center text-gray-400 font-[DMSans] text-xs-responsive">
          <small>Walky.me • Lyon, France</small>
          <small>Copyright © 2025</small>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PassCard;
